<template>
  <section
    class="login"
    @keyup.enter="loginUser">
    <img
      class="login__logo"
      src="~@/assets/img/SB-Images/app-logo.svg"
      alt="AI Number" >
    <h1>Sign in to AI Number account</h1>
    <div class="login-additionals">
      <span v-if="!inviteToken">Don't have an account?</span>
      <span v-else>By logging in you’ll join {{ organisation.name }}.</span>
      <router-link
        v-if="!inviteToken"
        :to="{ name: 'RegisterPage', query: $route.query }"
        tag="span"
        class="login__create-acc">
        Sign up
      </router-link>
    </div>
    <ValidationObserver
      ref="customFieldObserver"
      v-slot="{ valid, pristine }" >
      <div class="login-form">

        <!-- email form field -->
        <ValidationProvider
          v-slot="{ errors }"
          rules="required|email"
          name="Email"
          mode="eager" >
          <b-form-group
            label="Email"
            label-for="email">
            <b-form-input
              id="email"
              v-model="emailValue"
              name="email"
              type="email"
              placeholder="Enter your email"
              class="sb2-bordered sb2-hoverable sb2-focusable"
              :disabled="disableEmail"
              trim />
            <div
              v-if="errors[0]"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>{{ errors[0] }}</span>
            </div>
          </b-form-group>
        </ValidationProvider>

        <!-- password form field -->
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          name="Password" >
          <b-form-group
            label="Password"
            label-for="password">
            <b-form-input
              id="password"
              v-model="password"
              name="password"
              type="password"
              class="sb2-bordered sb2-hoverable sb2-focusable"
              placeholder="Enter your password"
              trim />
            <div
              v-if="errors[0]"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>{{ errors[0] }}</span>
            </div>
          </b-form-group>
        </ValidationProvider>
      </div>

      <div
        v-if="apiError && apiError.data"
        class="main-error flex center-all">
        <SBIcon
          class="mr-2"
          :icon="SBIconList.ERROR"
          :color="SBColors.redPrimary"
          :size="12" />
        <span>{{ apiError.data.message }}</span>
      </div>

      <b-button
        variant="primary"
        :disabled="sending || !valid || pristine"
        :class="{loading: sending}"
        class="flex center-all settings__actions--save"
        block
        @click="loginUser">
        <span>Sign in</span>
        <Spinner
          v-if="sending"
          :size="16"
          :thickness="2"
          :color="SBColors.white" />
      </b-button>

      <div class="small-sub text-center">
        <span>Forgot your password?</span>
        <router-link
          :to="{ name: 'ResetPasswordRequest' }"
          tag="span"
          class="login__forgot-pass">
          Reset password
        </router-link>
      </div>
    </ValidationObserver>
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { extend } from 'vee-validate';
import { email } from 'vee-validate/dist/rules';
import setValidationErrors from '@/helpers/validationErrors';

import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import Spinner from '@/components/common/Spinner';

extend('email', email);

export default {
  name: 'Login',
  components: {
    SBIcon,
    Spinner,
  },
  props: {
    inviteToken: {
      type: String,
      required: false,
      default: null,
    },
    email: {
      type: String,
      required: false,
      default: null,
    },
    organisation: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      emailValue: '',
      password: '',
      errorMessage: '',
      disableEmail: false,
      sending: false,
      validationErrors: {},
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      authenticated: 'user/authenticated',
      apiError: 'user/apiError',
    }),
  },
  watch: {
    authenticated(auth) {
      if (auth) {
        // this.sending = false; // we need to leave the spinner on the Sign in button until all the requirements finish
        if (this.inviteToken && this.inviteToken.length) {
          // this.acceptMerchantInvitation({ invite_token: this.inviteToken });
        }

        const nextPage = this.$route.query.next || sessionStorage.getItem('next');
        if (nextPage) {
          // oauth block
          sessionStorage.removeItem('next');
          window.location = nextPage;
          return;
        }
        // this.fetchMerchantSubscriptions();

        if (this.organisation && this.organisation.id) {
          // this.$router.push({ name: 'OrganisationWithId', params: { organisationId: this.organisation.id } }).catch(() => { });
          this.$router.push({ name: 'OrgContacts', params: { organisationId: this.organisation.id } }).catch(() => { });
        } else {
          // this.$router.push({ name: 'Organisation' }).catch(() => { });
          this.$router.push({ name: 'OrgContacts', params: {} }).catch(() => { });
        }
      }
    },
    apiError(error) {
      this.sending = false;
      if (error.status === 422) {
        // Refactor with new validator for v2
        this.validationErrors = setValidationErrors(error.data.errors);
      } else {
        this.errorMessage = error.data.message;
      }
    },
  },
  beforeMount() {
    this.finalizeSplashView();
  },
  mounted() {
    if (this.email) {
      this.emailValue = this.email;
      this.disableEmail = true;
    }
  },
  methods: {
    ...mapActions({
      login: 'user/login',
      acceptMerchantInvitation: 'user/acceptMerchantInvitation',
      // fetchMerchantSubscriptions: 'merchant/fetchSubscriptions',
      finalizeSplashView: 'organisation/finalizeSplashView',
    }),
    loginUser() {
      this.errorMessage = '';
      this.validationErrors = {};
      if (this.$refs.customFieldObserver.flags.valid) {
        this.sending = true;
        const userData = {
          email: this.emailValue,
          password: this.password,
        };

        this.login(userData);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.login {
  &__login-btn {
    height: 44px;
    font-size: 16px;
    margin: 30px 0 18px;
    text-transform: capitalize;

    .loader {
      margin: 0 4px 0 0;
      width: unset;
    }
  }
}

.settings__actions--save {
  margin-top: 30px;
  height: 44px;
}

.small-sub {
  margin-top: 16px;
  font-size: 14px !important;
  font-weight: 400 !important;
  color: $blackUltralight !important;
}
</style>
